import React, {useEffect, useRef} from "react";

import {AugmentedEvent, SimulationEventType} from "@repo/shared";
import {Color, CustomBlending, MaxEquation, OneFactor} from "three";

import {useGame} from "~/contexts/game-manager/GameManagerProvider";

import {ParticleEffect, ParticleEffectRef} from "../particle-effect";

const maxParticleAmount = 200;
const zIndex = 50;

/**
 * Renders a particle burst effect when the primary player collides with a physical line.
 */
const ImpulseEffect: React.FC = () => {
  const {gameManager} = useGame();
  const particleEffectRef = useRef<ParticleEffectRef>(null);

  // --- Event Handling Effect ---
  useEffect(() => {
    // Do nothing if the gameManager isn't ready yet.
    if (!gameManager) return;

    // This function will be called for every event from the simulation.
    const handleSimulationEvent = (event: AugmentedEvent) => {
      // 1. We only care about events from the primary simulation.
      if (!event.isPrimary) return;

      // 2. We only care about physical line collisions.
      if (event.type === SimulationEventType.PlayerHitLine) {
        // All the data we need is on the event object.
        const {contact, material} = event;
        if (!contact || !material) return;

        // 3. Re-calculate the impulse magnitude based on the event data.
        // This mirrors the physics calculation in the SimulationManager.
        // We only want to show an effect if the player was moving towards the line.
        if (contact.velocityAlongNormal < 0) {
          const impulseMag =
            -contact.velocityAlongNormal * (1 + material.restitution);

          // 4. Emit a particle effect based on the collision strength.
          // Using Math.log provides a nice visual curve where stronger impulses have diminishing returns.
          const size = Math.log(1 + impulseMag) * 0.5;

          const relPosition = event.player.currentPosition
            .clone()
            .sub(contact.position)
            .normalize()
            .mul(-event.player.radius);
          const spawnPosition = event.player.currentPosition
            .clone()
            .add(relPosition);

          particleEffectRef.current?.emit({
            x: spawnPosition.x,
            y: spawnPosition.y,
            count: 1, // A single, expanding particle burst
            // The size curve now makes the particle grow from nothing to its calculated size.
            size: [
              {stop: 0, size: 0},
              {stop: 1, size: size * 0.3},
            ],
            // The particle lifetime is also proportional to the impact strength.
            particleLifetime: Math.max(0.2, size * 0.8),
          });
        }
      }
    };

    // Subscribe to the event stream.
    gameManager.on("simulation-event", handleSimulationEvent);

    // Return the cleanup function to unsubscribe.
    return () => {
      gameManager.off("simulation-event", handleSimulationEvent);
    };
  }, [gameManager]); // Dependency array ensures we only subscribe once.

  return (
    <ParticleEffect
      ref={particleEffectRef}
      // These are now default parameters for the `emit` call, which can be overridden.
      sizeCurve={[
        {stop: 0, size: 0},
        {stop: 1, size: 1},
      ]}
      colorCurve={[
        {stop: 0, color: new Color("#FFFFFF")},
        {stop: 1, color: new Color("#000000")},
      ]}
      particleLifetime={0.5}
      maxParticles={maxParticleAmount}
      zIndex={zIndex}
      gravity={0}
      velocityMultiplier={0}
      upwardBias={0.0}
    >
      <circleGeometry args={[1, 24]} />
      <meshBasicMaterial
        color={"white"}
        blending={CustomBlending}
        depthWrite={false}
        transparent={true}
        blendSrc={OneFactor}
        blendDst={OneFactor}
        blendEquation={MaxEquation}
      />
    </ParticleEffect>
  );
};

export default ImpulseEffect;
